import React, { useState, useEffect } from 'react';
import DescriptionListItem from 'components/v2/DescriptionListItem';
import classNames from 'classnames';
import EditableSection from 'components/v2/EditableSection';
import Select from 'components/v2/select';
import Label from 'components/v2/forms/Label';
import TextField from 'components/v2/forms/TextField';
import CargomaticTooltip from 'components/v2/Tooltip';
import { useTranslation } from 'react-i18next';
import SessionStore from 'stores/SessionStore';
import { sluggify } from 'utils/localization/utils';
import RadioList from 'components/v2/RadioList';
import FormGroup from 'components/v2/FormGroup';
import Input from 'components/v2/input';
import { humanize } from 'underscore.string';
import { showToast } from 'stores/ToastStore';
import { getUserInvoiceSettings, upsertUserInvoiceSettings, getPendingAccessorialInclusion, upsertPendingAccessorialInclusion, } from 'utils/UserServiceAPIUtils';
import CustomDropdown from 'components/v2/dropdowns/CustomDropdown';
import { getDeliveryFormats, CHARGES_BY_GROUPING, } from 'constants/InvoiceDelivery';
import { CONFIGURED_FEATURES, featureEnabled } from 'helpers/feature-flags';
import { checkPermissonForEditShipperBillingModule } from '../utils';
const NOT_SPECIFIED = 'NOT_SPECIFIED';
const INVOICE_METHOD_DESCRIPTION_SLUGS = {
    ONE_PER_SHIPMENT: 'ONE_PER_SHIPMENT_DELIVERY_METHOD_DESCRIPTION',
    ONE_PER_SHIPMENT_PER_CHARGE_TYPE: 'ONE_PER_SHIPMENT_PER_CHARGE_TYPE_DELIVERY_METHOD_DESCRIPTION',
    TWO_PER_SHIPMENT: 'TWO_PER_SHIPMENT_DELIVERY_METHOD_DESCRIPTION',
    UNLIMITED: 'UNLIMITED_DELIVERY_METHOD_DESCRIPTION',
};
const brandingOptions = [
    {
        name: 'Cargomatic',
        slug: 'cargomatic',
        localizationSlug: 'CARGOMATIC',
    },
    {
        name: 'Atlas Marine',
        slug: 'atlas_marine',
        localizationSlug: 'ATLAS_MARINE',
    },
    {
        name: 'West End',
        slug: 'west_end',
        localizationSlug: 'WEST_END',
    },
    {
        name: 'Three Rivers',
        slug: 'three_rivers',
        localizationSlug: 'THREE_RIVERS',
    },
    {
        name: 'xlr_8',
        slug: 'xlr_8',
        localizationSlug: 'XLR_8',
    },
    {
        name: 'BP transportation',
        slug: 'bp_transportation',
        localizationSlug: 'BP_TRANSPORTATION',
    },
    {
        name: 'Itg',
        slug: 'itg',
        localizationSlug: 'ITG',
    },
];
const invoiceIdOptions = [
    {
        name: 'Suffix',
        slug: 'suffix',
        localizationSlug: 'PREVIOUS_ID_WITH_SUFFIX',
    },
    {
        name: 'Unique Id',
        slug: 'unique_id',
        localizationSlug: 'UNIQUE_ID',
    },
];
const invoiceAddressOptions = [
    {
        name: 'Complete Address',
        slug: 'complete_address',
        localizationSlug: 'COMPLETE_ADDRESS',
    },
    {
        name: 'City/State',
        slug: 'city_state',
        localizationSlug: 'CITY_STATE',
    },
];
const getInvoicePages = (pageSetup = []) => {
    var _a;
    return (_a = pageSetup.find((p = {}) => p.shipment_type === 'drayage')) === null || _a === void 0 ? void 0 : _a.page_count;
};
const getInvoiceGroupingOptions = (t) => {
    return [
        {
            id: 'ONE_PER_SHIPMENT',
            name: 'One Invoice per Shipment',
            description: () => {
                return (React.createElement("span", null, t(INVOICE_METHOD_DESCRIPTION_SLUGS.ONE_PER_SHIPMENT)));
            },
        },
        {
            id: 'ONE_PER_SHIPMENT_PER_CHARGE_TYPE',
            name: 'One invoice per Shipment per Charge Type',
            description: () => {
                return (React.createElement("span", null, t(INVOICE_METHOD_DESCRIPTION_SLUGS.ONE_PER_SHIPMENT_PER_CHARGE_TYPE)));
            },
        },
        {
            id: 'TWO_PER_SHIPMENT',
            name: 'Two Invoices per Shipment',
            description: () => {
                return (React.createElement("span", null, t(INVOICE_METHOD_DESCRIPTION_SLUGS.TWO_PER_SHIPMENT)));
            },
        },
        {
            id: 'UNLIMITED',
            name: 'Multiple invoices per Shipment',
            description: () => {
                return React.createElement("span", null, t(INVOICE_METHOD_DESCRIPTION_SLUGS.UNLIMITED));
            },
        },
    ];
};
const renderInvoiceDeliverySummary = (settings) => {
    const { t } = useTranslation();
    const { invoice_grouping = null, invoice_delivery_configs = [] } = settings || {};
    const tooltipContent = invoice_grouping
        ? INVOICE_METHOD_DESCRIPTION_SLUGS[invoice_grouping.toUpperCase()]
        : NOT_SPECIFIED;
    return (React.createElement(React.Fragment, null,
        React.createElement("h3", { className: "mb-4" }, t('INVOICE_GROUPING/DELIVERY_FORMAT')),
        React.createElement(DescriptionListItem, { label: t('INVOICE_GROUPING'), className: "mb-4" },
            React.createElement("div", { className: "flex items-center space-x-1" },
                React.createElement("span", null, invoice_grouping
                    ? humanize(t(invoice_grouping))
                    : t(NOT_SPECIFIED)),
                invoice_grouping && (React.createElement(CargomaticTooltip, { position: "top" }, t(tooltipContent))))),
        React.createElement("div", { className: "grid grid-cols-2 gap-4" }, invoice_delivery_configs.length ? (invoice_delivery_configs.map((config) => {
            const chargeLabel = humanize(t(config.charge_type));
            const method = humanize(t(config.invoicing_method));
            return (React.createElement(DescriptionListItem, { label: chargeLabel, className: "" }, method));
        })) : (React.createElement(DescriptionListItem, { label: t('DELIVERY_METHOD'), className: "" }, t(NOT_SPECIFIED))))));
};
const renderBillingStatusSummary = (includePendingAccessorials) => {
    const { t } = useTranslation();
    return (React.createElement(React.Fragment, null,
        React.createElement("h3", { className: "mb-4" }, t('BILLING_STATUS_EVALUATION')),
        React.createElement("div", { className: "grid grid-cols-2 gap-4" },
            React.createElement(DescriptionListItem, { label: t('INCLUDE_PENDING_ACCESSORIALS'), className: "" },
                React.createElement("span", null, includePendingAccessorials ? t('YES') : t('NO'))))));
};
const DrayageInvoiceSettingsSummary = ({ fields = {}, invoiceDeliverySettings = {}, includePendingAccessorials, }) => {
    var _a, _b;
    const { t } = useTranslation();
    const invoice_pages = getInvoicePages(fields.page_setup);
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: "grid grid-cols-2 gap-4" },
            React.createElement(DescriptionListItem, { label: t('GROUP_LIKE_ITEMS'), className: "" }, fields.group_like_invoice_items),
            React.createElement(DescriptionListItem, { label: t('INVOICE_BRANDING'), className: "" }, t(((_a = brandingOptions.find((o) => o.slug === (fields === null || fields === void 0 ? void 0 : fields.drayage_invoice_branding))) === null || _a === void 0 ? void 0 : _a.localizationSlug) || '')),
            React.createElement(DescriptionListItem, { label: t('NUMBER_OF_PAGES') }, invoice_pages),
            React.createElement(DescriptionListItem, { label: t('BILLING_TIMEFRAME_DAYS'), className: "" }, fields.drayage_blling_time_frame),
            React.createElement(DescriptionListItem, { label: t('INVOICE_ID_SETTINGS'), info: t('INVOICE_ID_SETTINGS_TOOLTIP'), position: "top", className: "" }, t(sluggify(fields.invoice_id_settings))),
            React.createElement(DescriptionListItem, { label: t('INVOICE_ADDRESS_SETTINGS'), info: t('INVOICE_ADDRESS_SETTINGS_TOOLTIP'), position: "top", className: "" }, t(((_b = invoiceAddressOptions.find((o) => o.slug === (fields === null || fields === void 0 ? void 0 : fields.invoice_address_settings))) === null || _b === void 0 ? void 0 : _b.localizationSlug) || ''))),
        featureEnabled(CONFIGURED_FEATURES.SSR) && (React.createElement(React.Fragment, null,
            React.createElement("hr", { className: "my-4" }),
            ' ',
            renderInvoiceDeliverySummary(invoiceDeliverySettings))),
        featureEnabled(CONFIGURED_FEATURES.SSR) && (React.createElement(React.Fragment, null,
            React.createElement("hr", { className: "my-4" }),
            ' ',
            renderBillingStatusSummary(includePendingAccessorials)))));
};
const DrayageInvoiceSettingsForm = ({ fields, onChange, saving, onSaveShipper, onSaveSuccess, onSaveFail, userId, invoiceDeliverySettings = {}, handleSaveInvoiceDeliverySettings, handleInvoiceDeliverySettingsSuccess, includePendingAccessorials, handleSetIncludePendingAccessorials, handleSavePendingAccessorialInclusion, }) => {
    const { t } = useTranslation();
    const [invoiceGrouping, setInvoiceGrouping] = useState(invoiceDeliverySettings === null || invoiceDeliverySettings === void 0 ? void 0 : invoiceDeliverySettings.invoice_grouping);
    const [invoiceDeliveryMethods, setInvoiceDeliveryMethods] = useState([]);
    const isFinanceAdmin = SessionStore.isFinanceAdmin();
    const isShipperOnboarder = SessionStore.hasShipperOnboardingRole();
    const invoice_pages = getInvoicePages(fields.page_setup);
    const options = [
        {
            value: 'yes',
            name: 'Yes',
            localizationSlug: 'YES',
        },
        {
            value: 'no',
            name: 'No',
            localizationSlug: 'NO',
        },
    ];
    const numberOfPagesOptions = [
        {
            value: '1',
            name: 'One: All Charges together',
            localizationSlug: 'INVOICE_PAGE_OPTION_ONE',
        },
        {
            value: '2',
            name: 'Two: Drayage and FSC on one, All other accessorial on two',
            localizationSlug: 'INVOICE_PAGE_OPTION_TWO',
        },
    ];
    const saveForm = async () => {
        try {
            await onSaveShipper();
            onSaveSuccess(fields);
            await onSaveInvoiceDeliverySettings();
            handleInvoiceDeliverySettingsSuccess();
        }
        catch (e) {
            onSaveFail();
        }
    };
    const onSaveInvoiceDeliverySettings = async () => {
        var _a, _b;
        if (!invoiceGrouping)
            return null;
        const mutation = {
            invoice_grouping: invoiceGrouping.id,
            invoice_delivery_configs: [...invoiceDeliveryMethods],
        };
        const result = await handleSaveInvoiceDeliverySettings(mutation);
        if ((_b = (_a = result === null || result === void 0 ? void 0 : result.data) === null || _a === void 0 ? void 0 : _a.upsertInvoiceSetting) === null || _b === void 0 ? void 0 : _b.success) {
            await onSavePendingAccessorialInclusion(invoiceGrouping.id);
        }
    };
    const onSavePendingAccessorialInclusion = (invoiceGrouping) => {
        return handleSavePendingAccessorialInclusion(invoiceGrouping);
    };
    useEffect(() => {
        setInitialEditData();
    }, [userId]);
    const setInitialEditData = () => {
        if (!invoiceDeliverySettings) {
            setInvoiceGrouping(null);
            return;
        }
        const { invoice_grouping, invoice_delivery_configs } = invoiceDeliverySettings;
        const options = getInvoiceGroupingOptions(t);
        const selectedInvoiceGrouping = invoice_grouping
            ? options.find((option) => option.id.toUpperCase() === invoice_grouping)
            : null;
        setInvoiceGrouping(selectedInvoiceGrouping);
        const methods = invoice_delivery_configs
            ? invoice_delivery_configs.map((r) => {
                return {
                    charge_type: r.charge_type,
                    invoicing_method: r.invoicing_method,
                };
            })
            : [];
        setInvoiceDeliveryMethods(methods);
    };
    const handleInvoiceDeliverySettingsChange = (change) => {
        setInvoiceDeliveryMethods(change);
    };
    const handleInvoiceGroupingChange = (change) => {
        setInvoiceGrouping(change);
        setInvoiceDeliveryMethods([]);
    };
    const invoiceGroup = invoiceGrouping === null || invoiceGrouping === void 0 ? void 0 : invoiceGrouping.id;
    const canEditIncludePendingAccessorials = invoiceGroup !== 'UNLIMITED' && invoiceGroup !== 'ONE_PER_SHIPMENT';
    useEffect(() => {
        if (saving) {
            saveForm();
        }
    }, [saving]);
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: "grid grid-cols-2 gap-4" },
            React.createElement("div", null,
                React.createElement(Label, { htmlFor: "groupInvoiceItems" }, t('GROUP_LIKE_ITEMS')),
                React.createElement(Select, { items: options, valueKey: "name", textKey: "localizationSlug", selected: fields.group_like_invoice_items, onSelect: ({ name }) => onChange('group_like_invoice_items', name) })),
            React.createElement("div", null,
                React.createElement(Label, { htmlFor: "bilingTimeFrame" }, t('INVOICE_BRANDING')),
                React.createElement(Select, { items: brandingOptions, valueKey: "slug", textKey: "localizationSlug", selected: fields.drayage_invoice_branding, onSelect: ({ slug }) => onChange('drayage_invoice_branding', slug) })),
            React.createElement("div", { className: "" },
                React.createElement(Label, { htmlFor: "bilingTimeFrame" }, t('BILLING_TIMEFRAME_DAYS')),
                React.createElement(TextField, { value: fields.drayage_blling_time_frame, onChange: (val) => onChange('drayage_blling_time_frame', val) })),
            React.createElement("div", { className: "col-span-2" },
                React.createElement(Label, null, t('NUMBER_OF_PAGES')),
                React.createElement(Select, { items: numberOfPagesOptions, valueKey: "value", textKey: "localizationSlug", className: "z-10", selected: `${invoice_pages}`, onSelect: (option) => {
                        onChange('invoice_pages', option === null || option === void 0 ? void 0 : option.value);
                    } })),
            React.createElement("div", null, isFinanceAdmin && (React.createElement(Select, { items: invoiceIdOptions, label: t('INVOICE_ID'), info: t('INVOICE_ID_SETTINGS_TOOLTIP'), valueKey: "slug", textKey: "localizationSlug", selected: fields.invoice_id_settings, onSelect: ({ slug }) => onChange('invoice_id_settings', slug) }))),
            React.createElement("div", null,
                React.createElement(Select, { items: invoiceAddressOptions, label: t('INVOICE_ADDRESS_SETTINGS'), info: t('INVOICE_ADDRESS_SETTINGS_TOOLTIP'), valueKey: "slug", textKey: "localizationSlug", selected: fields.invoice_address_settings, onSelect: ({ slug }) => onChange('invoice_address_settings', slug) }))),
        featureEnabled(CONFIGURED_FEATURES.SSR) && isShipperOnboarder && (React.createElement(EditInvoiceDeliverySettingsForm, { invoiceDeliverySettings: invoiceDeliverySettings, handleInvoiceDeliverySettingsChange: handleInvoiceDeliverySettingsChange, invoiceGrouping: invoiceGrouping, setInvoiceDeliveryMethods: setInvoiceDeliveryMethods, invoiceDeliveryMethods: invoiceDeliveryMethods, setInvoiceGrouping: handleInvoiceGroupingChange })),
        featureEnabled(CONFIGURED_FEATURES.SSR) &&
            canEditIncludePendingAccessorials &&
            isShipperOnboarder && (React.createElement(EditBillingStatusEvaluationForm, { includePendingAccessorials: includePendingAccessorials, invoiceGrouping: invoiceDeliverySettings === null || invoiceDeliverySettings === void 0 ? void 0 : invoiceDeliverySettings.invoice_grouping, handleSetIncludePendingAccessorials: handleSetIncludePendingAccessorials }))));
};
const EditInvoiceDeliverySettingsForm = ({ invoiceDeliverySettings, handleInvoiceDeliverySettingsChange, setInvoiceDeliveryMethods, setInvoiceGrouping, invoiceDeliveryMethods = [], invoiceGrouping, }) => {
    const { t } = useTranslation();
    const deliveryFormatOptions = getDeliveryFormats(t);
    const handleInvoiceDeliveryGroupingChange = (method) => {
        setInvoiceGrouping(method);
    };
    const handleDeliveryMethodChange = (change, type) => {
        const method = change.value;
        const previousMethods = invoiceDeliveryMethods.filter((m) => m.charge_type.toUpperCase() !== type.toUpperCase());
        const newMethod = {
            charge_type: type.toUpperCase(),
            invoicing_method: method.toUpperCase(),
        };
        const changedMethods = [...previousMethods, newMethod];
        setInvoiceDeliveryMethods([...previousMethods, newMethod]);
        handleInvoiceDeliverySettingsChange(changedMethods);
    };
    const getDeliveryMethodCharges = (grouping) => {
        if (!grouping)
            return null;
        const chargeTypes = CHARGES_BY_GROUPING[grouping.toUpperCase()];
        return chargeTypes || null;
    };
    const renderDeliveryMethodOptions = (grouping) => {
        const chargeTypes = getDeliveryMethodCharges(grouping);
        if (!chargeTypes) {
            return null;
        }
        return (React.createElement("div", { className: "grid grid-cols-2 gap-4" }, chargeTypes.map((chargeType) => {
            const label = humanize(t(chargeType));
            const currentValue = invoiceDeliveryMethods.find((i) => i.charge_type === chargeType.toUpperCase());
            const selected = currentValue
                ? deliveryFormatOptions.find((o) => o.value.toUpperCase() === currentValue.invoicing_method)
                : null;
            return (React.createElement(FormGroup, { label: label, className: "" },
                React.createElement(CustomDropdown, { label: "", options: deliveryFormatOptions, onChange: (change) => handleDeliveryMethodChange(change, chargeType), value: selected, nameKey: "name", valueKey: "value" })));
        })));
    };
    const invoiceGroupingOptions = getInvoiceGroupingOptions(t);
    const selectedGrouping = invoiceGrouping
        ? invoiceGroupingOptions.find((option) => option.id === invoiceGrouping.id)
        : null;
    return (React.createElement(React.Fragment, null,
        React.createElement("hr", { className: "my-4" }),
        React.createElement(FormGroup, { label: t('INVOICE_GROUPING'), className: "mt-4 col-span-full" },
            React.createElement(RadioList, { label: t('INVOICE_GROUPING'), options: invoiceGroupingOptions, selected: selectedGrouping, onSelect: (method) => handleInvoiceDeliveryGroupingChange(method) })),
        React.createElement("label", { className: "mt-4 mb-2" }, t('INVOICE_DELIVERY_FORMATS')),
        selectedGrouping && renderDeliveryMethodOptions(invoiceGrouping.id)));
};
const EditBillingStatusEvaluationForm = ({ includePendingAccessorials, invoiceGrouping, handleSetIncludePendingAccessorials, }) => {
    const { t } = useTranslation();
    return (React.createElement(React.Fragment, null,
        React.createElement("hr", { className: "my-4" }),
        React.createElement(FormGroup, { label: t('BILLING_STATUS_EVALUATION'), className: "mt-4 col-span-full" },
            React.createElement("div", { className: "flex items-center space-x-1" },
                React.createElement(Input, { className: "my-4", type: "checkbox", label: t('INCLUDE_PENDING_ACCESSORIALS'), checked: includePendingAccessorials, onChange: handleSetIncludePendingAccessorials }),
                React.createElement(CargomaticTooltip, { position: "top" }, t('BILLING_STATUS_EVALUATION_DESCRIPTION'))))));
};
const DrayageInvoiceSettings = ({ data, className, onUpdateShipper }) => {
    const [initialData, setInitialData] = useState(data);
    const [drayageInvoiceSettingsData, setDrayageInvoiceSettingsData] = useState(data);
    const [invoiceDeliverySettings, setInvoiceDeliverySettings] = useState(null);
    const [includePendingAccessorials, setIncludePendingAccessorials] = useState(true);
    const { t } = useTranslation();
    const getInitData = () => {
        if (data) {
            setInitialData(data);
            setDrayageInvoiceSettingsData(data);
        }
    };
    const onChange = (field, value) => {
        if (field === 'invoice_pages') {
            setDrayageInvoiceSettingsData((prev) => {
                let page_setup = prev.page_setup || [];
                const updatedPageSetup = page_setup.map((p) => {
                    if (p.shipment_type === 'drayage') {
                        p.page_count = value;
                    }
                    return p;
                });
                return Object.assign(Object.assign({}, prev), { page_setup: updatedPageSetup });
            });
        }
        else {
            setDrayageInvoiceSettingsData((prev) => (Object.assign(Object.assign({}, prev), { [field]: value })));
        }
    };
    const handleSave = async () => {
        const updates = {};
        Object.keys(initialData).forEach((key) => {
            if (!(initialData[key] === drayageInvoiceSettingsData[key])) {
                updates[key] = drayageInvoiceSettingsData[key];
            }
            if (key === 'page_setup') {
                delete updates.page_setup;
                updates.invoice_preferences = {
                    page_setup: drayageInvoiceSettingsData[key],
                };
            }
        });
        if (Object.keys(updates).length) {
            const payload = Object.assign({}, updates);
            if (updates.group_like_invoice_items) {
                payload.group_like_invoice_items =
                    updates.group_like_invoice_items === 'Yes';
            }
            await onUpdateShipper(payload);
        }
    };
    const handleAfterSave = (onSaveSuccess) => {
        return (updatedData = {}) => {
            setInitialData((prev) => (Object.assign(Object.assign({}, prev), updatedData)));
            onSaveSuccess();
        };
    };
    const handleInvoiceDeliverySettingsSuccess = () => {
        fetchUserBillingSettings();
    };
    const handleSaveInvoiceDeliverySettings = async (mutation) => {
        try {
            return await upsertUserInvoiceSettings(data.userId, mutation);
        }
        catch (e) {
            showToast(t('ERROR_SAVING_INVOICE_DELIVERY_SETTINGS'), null, 'error');
            console.log('error saving invoice settings', e.message);
        }
    };
    const determinePendingAccessorialsInclusionToSave = (invoiceGrouping) => {
        if (invoiceGrouping === 'UNLIMITED') {
            return false;
        }
        else if (invoiceGrouping === 'ONE_PER_SHIPMENT') {
            return true;
        }
        else {
            return includePendingAccessorials;
        }
    };
    const handleSavePendingAccessorialInclusion = async (invoiceGrouping) => {
        try {
            const statusToSave = determinePendingAccessorialsInclusionToSave(invoiceGrouping);
            await upsertPendingAccessorialInclusion(data.userId, statusToSave);
        }
        catch (e) {
            showToast(t('ERROR_SAVING_PENDING_ACCESSORIAL_INCLUSION'), null, 'error');
            console.error('error saving pending accessorial inclusion settings', e.message);
        }
    };
    useEffect(() => {
        fetchUserBillingSettings();
    }, [data.userId]);
    const determineIncomingPendingAccessorialsInclusion = (invoiceGrouping, pendingAccessorialInclusion) => {
        if (invoiceGrouping === 'UNLIMITED') {
            return false;
        }
        else if (invoiceGrouping === 'ONE_PER_SHIPMENT') {
            return true;
        }
        else {
            return pendingAccessorialInclusion;
        }
    };
    const fetchUserBillingSettings = async () => {
        var _a, _b, _c, _d;
        try {
            const result = await getUserInvoiceSettings(data.userId);
            const settings = (_b = (_a = result === null || result === void 0 ? void 0 : result.data) === null || _a === void 0 ? void 0 : _a.getInvoiceSetting) === null || _b === void 0 ? void 0 : _b.invoice_setting;
            const pendingAccessorialsInclusionStatus = await getPendingAccessorialInclusion(data.userId);
            const pendingAccessorialInclusion = (_d = (_c = pendingAccessorialsInclusionStatus.data) === null || _c === void 0 ? void 0 : _c.getBillReadyStatusCalculation) === null || _d === void 0 ? void 0 : _d.include_pending_accessorials_in_calculation;
            const defaultPendingAccessorialsInclusion = determineIncomingPendingAccessorialsInclusion(settings === null || settings === void 0 ? void 0 : settings.invoice_grouping, pendingAccessorialInclusion);
            setInvoiceDeliverySettings(settings);
            setIncludePendingAccessorials(defaultPendingAccessorialsInclusion);
        }
        catch (e) {
            console.log('Error fetching userInvoiceSettings', e.message);
        }
    };
    useEffect(() => {
        getInitData();
    }, []);
    return (React.createElement(EditableSection, { title: t('INVOICE'), className: classNames(className), canEdit: checkPermissonForEditShipperBillingModule() }, (editing, saving, onSaveSuccess, onSaveFail) => (React.createElement(React.Fragment, null,
        !editing && (React.createElement(DrayageInvoiceSettingsSummary, { fields: initialData, invoiceDeliverySettings: invoiceDeliverySettings, includePendingAccessorials: includePendingAccessorials })),
        editing && (React.createElement(DrayageInvoiceSettingsForm, { fields: drayageInvoiceSettingsData, onChange: onChange, onSaveShipper: () => handleSave(), saving: saving, onSaveSuccess: handleAfterSave(onSaveSuccess), onSaveFail: onSaveFail, invoiceDeliverySettings: invoiceDeliverySettings, userId: data.userId, handleSaveInvoiceDeliverySettings: handleSaveInvoiceDeliverySettings, handleInvoiceDeliverySettingsSuccess: handleInvoiceDeliverySettingsSuccess, includePendingAccessorials: includePendingAccessorials, handleSetIncludePendingAccessorials: setIncludePendingAccessorials, handleSavePendingAccessorialInclusion: handleSavePendingAccessorialInclusion }))))));
};
export default DrayageInvoiceSettings;
